// ========== src/utils/api.js ==========

const API_URL = process.env.REACT_APP_API_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Auth
export const login = async (email, password) => {
  const response = await fetch(`${API_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  return response.json();
};

export const register = async (userData) => {
  const response = await fetch(`${API_URL}/api/v1/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData)
  });
  return response.json();
};

// Games
export const generateGame = async (gameType, params) => {
  const response = await fetch(`${API_URL}/api/v1/ai-games/${gameType}`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(params)
  });

  if (response.status === 429) {
    throw new Error('AI_LIMIT_EXCEEDED');
  }

  return response.json();
};

export const getMyGames = async (limit = 10) => {
  const response = await fetch(`${API_URL}/api/v1/ai-games/my-games?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const completeGame = async (gameId, score, timeSpent) => {
  const response = await fetch(`${API_URL}/api/v1/ai-games/${gameId}/complete`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify({ score, time_spent: timeSpent })
  });
  return response.json();
};

// Subscription
export const getUsage = async () => {
  const response = await fetch(`${API_URL}/api/v1/subscription/usage`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const getPlans = async () => {
  const response = await fetch(`${API_URL}/api/v1/subscription/plans`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const createCheckout = async (tier, billingCycle = 'monthly', childId = null) => {
  const body = { tier, billing_cycle: billingCycle };
  if (childId) {
    body.child_id = childId;
  }
  
  const response = await fetch(`${API_URL}/api/v1/subscription/create-checkout`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(body)
  });
  return response.json();
};

// Parent
export const getChildren = async () => {
  const response = await fetch(`${API_URL}/api/v1/parent/children`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const addChild = async (childData) => {
  const response = await fetch(`${API_URL}/api/v1/parent/children`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(childData)
  });
  return response.json();
};

export const getChildStats = async (childId) => {
  const response = await fetch(`${API_URL}/api/v1/parent/child/${childId}/stats`, {
    headers: getAuthHeader()
  });
  return response.json();
};

// Admin
export const getPlatformStats = async () => {
  const response = await fetch(`${API_URL}/api/v1/dashboard/admin`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const getRecentUsers = async (limit = 10) => {
  const response = await fetch(`${API_URL}/api/v1/admin/recent-users?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const getSubscriptionStats = async () => {
  const response = await fetch(`${API_URL}/api/v1/admin/subscription-stats`, {
    headers: getAuthHeader()
  });
  return response.json();
};

export const resetChildPassword = async (childId, newPassword) => {
  const response = await fetch(`${API_URL}/api/v1/parent/children/${childId}/password`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ new_password: newPassword })
  });
  return response.json();
};

// ── Generic helpers ───────────────────────────────────────────────────────────

export const get = async (endpoint) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    headers: getAuthHeader(),
  });
  if (!response.ok) throw new Error(`GET ${endpoint} failed: ${response.status}`);
  return response.json();
};

export const post = async (endpoint, body = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`POST ${endpoint} failed: ${response.status}`);
  return response.json();
};

// ─────────────────────────────────────────────────────────────────────────────

const api = {
  get,
  post,
  login,
  register,
  generateGame,
  getMyGames,
  completeGame,
  getUsage,
  getPlans,
  createCheckout,
  getChildren,
  addChild,
  resetChildPassword,
  getChildStats,
  getPlatformStats,
  getRecentUsers,
  getSubscriptionStats,
};

export default api;